import {
  AxiosError,
  type AxiosResponse,
  isAxiosError,
  type AxiosResponseTransformer
} from 'axios'

import agent from 'agent'

import type { UseInfiniteQueryOptions } from 'react-query'

/**
 * Types
 */
export type GremlinQueryParams = {
  teamId?: string
}

export type PaginatedQueryParams = {
  pageNumber?: string | null
  pageSize?: number | null
  pageToken?: number | null
}

export type PaginatedQueryOptions<TData> = UseInfiniteQueryOptions<
  TData,
  AxiosError,
  TData,
  TData
> &
  PaginatedQueryParams

/**
 * Constants
 */
// We derive the total counts from the paginated now-running queries and display the value up to "99+. It is therefore
// necessary to fetch a minimum pageSize of 100 results to ensure the UI can display the correct total count.
export const DEFAULT_NOW_RUNNING_QUERY_PAGE_SIZE = 100

// The GCP cloud resources endpoints have a maximum page size of 100
export const GCP_CLOUD_RESOURCES_QUERY_PAGE_SIZE_DEFAULT = 100

// These values are used for aggregating paginated queries into a single list of values
export const API_CALL_THROTTLE_TIME_MS = 50
export const PAGE_SIZE_DEFAULT = 500

/**
 * Utility Functions
 */
export function isGremlinQueryParams(
  queryParams: GremlinQueryParams
): queryParams is GremlinQueryParams {
  return 'teamId' in queryParams
}

export function isPaginatedQueryParams(
  queryParams: Record<string, string | number | null>
): queryParams is PaginatedQueryParams {
  return (
    'pageNumber' in queryParams ||
    'pageSize' in queryParams ||
    'pageToken' in queryParams
  )
}

/**
 * Build the endpoint for a query.
 */
export function getEndpointWithParams(
  endpoint: string,
  queryParams: Record<string, string | string[] | number | boolean | null>
) {
  const params = Object.entries(queryParams).reduce((params, [key, value]) => {
    if (value !== undefined && value !== null) {
      const valueString = Array.isArray(value)
        ? value.map((value) => value.replace(/\s/g, '')).join(',')
        : value.toString()

      params.set(key, valueString)
    }

    return params
  }, new URLSearchParams())

  return `${endpoint}?${params.toString()}`
}

/**
 * Allows an Axios request to quickly chain multiple response transformers that build on top of
 * the base Axios transformations responsible for parsing the initial response into JSON.
 */
export function transformAxiosResponse(
  transformers: AxiosResponseTransformer[] | AxiosResponseTransformer
) {
  return [
    // When we pass an AxiosRequestConfig with a transformResponse function, it overrides Axios
    // defaults, which originally parses the response as JSON. Mixing this default back into the
    // transformation array avoids having to parse text as JSON in a blocking manner.
    ...[agent.defaults.transformResponse ?? []],
    transformers
  ].flat()
}

/**
 * Guarantees an array is returned
 */
export function getPageDataItemsFlat<TPageDataItem>(dataPaged?: {
  pages?: { items: TPageDataItem[] }[]
}) {
  return (
    dataPaged?.pages?.flatMap((page) => page.items) ?? ([] as TPageDataItem[])
  )
}

/** Throws if any values in the provided object are falsy */
export function validateQueryDependencies(
  queryDeps: Record<string, any>,
  messageSuffix?: string
) {
  Object.entries(queryDeps).forEach(([key, value]) => {
    if (!value) {
      throw new AxiosError(`${key} is required ${messageSuffix}`)
    }
  })
}

export function throwIfEmptyResponse(
  response: AxiosResponse<{ errorMessage?: string }>
) {
  if (!isAxiosError(response) && response.status === 204) {
    const axiosError = new AxiosError(
      response.data.errorMessage,
      `${response.status}`,
      response.config,
      response.request,
      response
    )
    axiosError.cause = 'No Content'
    throw axiosError
  }
}

/**
 * Returns status code and range booleans if isAxiosError
 * See [HTTP response status codes - HTTP | MDN](https://developer.mozilla.org/en-US/docs/Web/HTTP/Status)
 */
export function getErrorStatusProperties<T, D>(error?: unknown) {
  if (isAxiosError<T, D>(error) && error?.response?.status) {
    const status = error.response.status

    return {
      status,
      is1xx: status >= 100 && status < 200,
      is2xx: status >= 200 && status < 300,
      is3xx: status >= 300 && status < 400,
      is4xx: status >= 400 && status < 500,
      is5xx: status >= 500 && status < 600
    }
  }
}

export function isAxios400Error(error: unknown) {
  const { is4xx } = getErrorStatusProperties(error) ?? {}

  return is4xx ?? false
}
